import { memo } from 'react';
import Noimage from '../../assets/noimage.png';
import { optimizeImage } from '../../utils/image';
import './AnnonceItem.css';

/**
 * Carte d'annonce — 100 % présentationnelle : AUCUN appel réseau ici.
 *
 * Toutes les données (note, avis, instantané, horaires hôte, indisponibilité)
 * viennent de l'endpoint /vehicleAnnonces/search-listing, en une seule requête
 * pour toute la page.
 *
 * Props :
 * - propsData        : annonce enrichie par l'API
 * - searchStart/End  : Date | null (dates de la recherche)
 * - currentUserId    : id de l'utilisateur connecté
 * - isSelected       : la voiture est dans "Mes demandes"
 * - isFavorite       : l'annonce est en favoris
 * - onToggleSelect   : (annonce) => void
 * - onToggleFavorite : (vehiculeAnnonceId) => void
 */

const isSameDay = (a, b) =>
  a.getDate() === b.getDate() &&
  a.getMonth() === b.getMonth() &&
  a.getFullYear() === b.getFullYear();

// JS : 0 = dimanche  →  app : 0 = lundi ... 6 = dimanche
const toAppWeekday = (date) => {
  if (!date) return null;
  const jsDay = date.getDay();
  return jsDay === 0 ? 6 : jsDay - 1;
};

const fmtTime = (time) => (time || '').slice(0, 5);

const getWindow = (schedule, date) => {
  const weekday = toAppWeekday(date);

  if (weekday === null || !schedule?.length) return null;

  const slot = schedule.find((item) => String(item.Weekday) === String(weekday));

  if (!slot) return null;

  return slot.alwaysAvailable
    ? 'Toute la journée'
    : `${fmtTime(slot.startTime)} – ${fmtTime(slot.endTime)}`;
};

function AnnonceItem({
  propsData,
  searchStart,
  searchEnd,
  currentUserId,
  isSelected,
  isFavorite,
  onToggleSelect,
  onToggleFavorite,
}) {
  const vehicle = propsData?.Vehicle || {};
  const annonceId = propsData?.vehiculeAnnonceId;
  const detailUrl = `/location-voiture/${annonceId}`;

  // ---------------------------------------------------------
  // Données d'affichage
  // ---------------------------------------------------------

  const firstImage = vehicle.images?.split(',').filter(Boolean)[0];
  const vehicleImage = firstImage ? optimizeImage(firstImage) : Noimage;

  const vehicleTitle =
    [vehicle.VehicleModel?.marque, vehicle.VehicleModel?.modele].filter(Boolean).join(' ') ||
    'Véhicule disponible';

  const price = propsData?.reservationPrice;
  const distance = propsData?.distance;
  const days = propsData?.nombreJours || 0;

  const rating = propsData?.rating ? Number(propsData.rating) : null;
  const reviewsCount = Number(propsData?.reviewsCount) || 0;
  const hasRating = Boolean(rating && reviewsCount);

  const isOwner = Boolean(currentUserId) && currentUserId === vehicle.userId;
  const isUnavailable = Boolean(propsData?.isUnavailable);

  // Réservation instantanée : si le propriétaire ne l'autorise pas,
  // on bloque uniquement les départs le jour même.
  const instantPreference = propsData?.isInstant !== false;
  const startsToday = Boolean(searchStart) && isSameDay(searchStart, new Date());
  const isInstant = instantPreference || !startsToday;

  const hostSchedule = propsData?.hostSchedule;
  const pickupWindow = searchStart ? getWindow(hostSchedule, searchStart) : null;
  const returnWindow = searchEnd ? getWindow(hostSchedule, searchEnd) : null;
  const showSchedule = Boolean(pickupWindow || returnWindow) && searchStart && searchEnd;

  const handleFavorite = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onToggleFavorite(annonceId);
  };

  const handleSelect = () => onToggleSelect(propsData);

  const ratingBlock = hasRating ? (
    <>
      <strong>{rating.toFixed(2)}</strong>
      <span>({reviewsCount})</span>
    </>
  ) : (
    <span>Nouveau</span>
  );

  return (
    <>
      {/* =====================================================
          DESKTOP
      ===================================================== */}

      <article className="mc-vehicle-card d-none d-md-flex">
        <div className="mc-vehicle-card-image">
          <a href={detailUrl} className="mc-vehicle-image-link">
            <img
              src={vehicleImage}
              alt={vehicleTitle}
              className="mc-vehicle-image"
              loading="lazy"
              decoding="async"
            />
          </a>

          <button
            type="button"
            className={`mc-vehicle-favorite ${isFavorite ? 'is-favorited' : ''}`}
            onClick={handleFavorite}
            aria-label={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          >
            <i className={`bi ${isFavorite ? 'bi-heart-fill' : 'bi-heart'}`} />
          </button>

          {!isInstant && (
            <span className="mc-vehicle-badge">
              <i className="bi bi-clock" />
              Sur rendez-vous
            </span>
          )}
        </div>

        <div className="mc-vehicle-card-content">
          <div className="mc-vehicle-main">
            <div className="mc-vehicle-heading">
              <div className="mc-vehicle-heading-main">
                <a href={detailUrl} className="mc-vehicle-title">
                  {vehicleTitle}
                </a>

                <div className="mc-vehicle-meta">
                  <span className="mc-vehicle-rating">
                    <i className="bi bi-star-fill" />
                    {ratingBlock}
                  </span>

                  <span className="mc-meta-separator">•</span>

                  <span>
                    <i className="bi bi-geo-alt" />
                    {distance} km
                  </span>
                </div>
              </div>

              <div className="mc-vehicle-price">
                <strong>{price} €</strong>
                <span>/ jour</span>
              </div>
            </div>

            <div className="mc-vehicle-info-grid">
              <div className="mc-vehicle-info">
                <div className="mc-vehicle-info-icon">
                  <i className="bi bi-calendar-check" />
                </div>

                <div>
                  <span>Disponibilité</span>
                  <strong>{isUnavailable ? 'Indisponible' : 'Disponible'}</strong>
                </div>
              </div>

              <div className="mc-vehicle-info">
                <div className="mc-vehicle-info-icon">
                  <i className="bi bi-clock" />
                </div>

                <div>
                  <span>Durée</span>
                  <strong>
                    {days} jour{days > 1 ? 's' : ''}
                  </strong>
                </div>
              </div>
            </div>

            {showSchedule && (
              <div className="mc-vehicle-schedule">
                <div className="mc-schedule-title">Horaires de remise et retour</div>

                <div className="mc-schedule-grid">
                  {pickupWindow && (
                    <div className="mc-schedule-item">
                      <i className="bi bi-box-arrow-in-right" />

                      <div>
                        <span>Remise des clés</span>
                        <strong>{pickupWindow}</strong>
                      </div>
                    </div>
                  )}

                  {returnWindow && (
                    <div className="mc-schedule-item">
                      <i className="bi bi-box-arrow-left" />

                      <div>
                        <span>Retour des clés</span>
                        <strong>{returnWindow}</strong>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!isInstant && (
              <div className="mc-vehicle-warning">
                <i className="bi bi-info-circle" />
                <span>Ce propriétaire n'autorise pas les réservations instantanées.</span>
              </div>
            )}
          </div>

          <div className="mc-vehicle-actions">
            <a href={detailUrl} className="mc-vehicle-details">
              Voir les détails
              <i className="bi bi-arrow-up-right" />
            </a>

            {isOwner ? (
              <span className="mc-vehicle-owner">
                <i className="bi bi-person-check" />
                Mon véhicule
              </span>
            ) : isUnavailable ? (
              <span className="mc-vehicle-unavailable">
                <i className="bi bi-x-circle" />
                Indisponible sur ces dates
              </span>
            ) : !isInstant ? (
              <span className="mc-vehicle-appointment">
                <i className="bi bi-calendar-event" />
                Réservation sur rendez-vous
              </span>
            ) : !isSelected ? (
              <button type="button" className="mc-vehicle-cta" onClick={handleSelect}>
                <i className="bi bi-plus-circle" />
                Demander une réservation
                <i className="bi bi-arrow-up-right" />
              </button>
            ) : (
              <button
                type="button"
                className="mc-vehicle-cta mc-vehicle-cta-danger"
                onClick={handleSelect}
              >
                <i className="bi bi-x-circle" />
                Annuler la demande
              </button>
            )}
          </div>
        </div>
      </article>

      {/* =====================================================
          MOBILE
      ===================================================== */}

      <article className="mc-mobile-vehicle-card d-block d-md-none">
        <div className="mc-mobile-vehicle-top">
          <div className="mc-mobile-vehicle-image">
            <a href={detailUrl}>
              <img src={vehicleImage} alt={vehicleTitle} loading="lazy" decoding="async" />
            </a>

            <button
              type="button"
              className={`mc-mobile-favorite ${isFavorite ? 'is-favorited' : ''}`}
              onClick={handleFavorite}
              aria-label={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            >
              <i className={`bi ${isFavorite ? 'bi-heart-fill' : 'bi-heart'}`} />
            </button>

            <span className="mc-mobile-price">
              {price} €<small>/ jour</small>
            </span>
          </div>

          <div className="mc-mobile-vehicle-content">
            <a href={detailUrl} className="mc-mobile-title">
              {vehicleTitle}
            </a>

            <div className="mc-mobile-rating">
              <i className="bi bi-star-fill" />
              {ratingBlock}
            </div>

            <div className="mc-mobile-location">
              <i className="bi bi-geo-alt" />
              {distance} km
            </div>

            <div className="mc-mobile-duration">
              <i className="bi bi-clock" />
              {days} jour{days > 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {showSchedule && (
          <div className="mc-mobile-schedule">
            {pickupWindow && (
              <div>
                <i className="bi bi-box-arrow-in-right" />
                <span>Remise</span>
                <strong>{pickupWindow}</strong>
              </div>
            )}

            {returnWindow && (
              <div>
                <i className="bi bi-box-arrow-left" />
                <span>Retour</span>
                <strong>{returnWindow}</strong>
              </div>
            )}
          </div>
        )}

        {!isInstant && (
          <div className="mc-mobile-warning">
            <i className="bi bi-clock-history" />
            Sur rendez-vous uniquement
          </div>
        )}

        <a href={detailUrl} className="mc-mobile-details">
          Voir les détails
          <i className="bi bi-chevron-right" />
        </a>

        <div className="mc-mobile-action">
          {isOwner ? (
            <div className="mc-mobile-owner">
              <i className="bi bi-person-check" />
              Mon véhicule
            </div>
          ) : isUnavailable ? (
            <div className="mc-mobile-unavailable">
              <i className="bi bi-x-circle" />
              Indisponible sur ces dates
            </div>
          ) : !isInstant ? (
            <div className="mc-mobile-appointment">
              <i className="bi bi-calendar-event" />
              Réservation sur rendez-vous
            </div>
          ) : !isSelected ? (
            <button type="button" className="mc-mobile-cta" onClick={handleSelect}>
              <i className="bi bi-plus-circle" />
              Demander une réservation
            </button>
          ) : (
            <button
              type="button"
              className="mc-mobile-cta mc-mobile-cta-danger"
              onClick={handleSelect}
            >
              <i className="bi bi-x-circle" />
              Annuler la demande
            </button>
          )}
        </div>
      </article>
    </>
  );
}

// memo : la carte ne se re-rend que si ses props changent réellement
export default memo(AnnonceItem);
